import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

// Toast Provider Component
export function PremiumToast({ type = 'info', title, message, correlationId }) {
  const glowColors = {
    success: 'from-emerald-500',
    error: 'from-red-500',
    warning: 'from-amber-500',
    info: 'from-violet-500',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      className="glass-card rounded-xl p-4 min-w-[300px] flex items-start gap-3"
    >
      <div className={`w-1 h-full min-h-[40px] rounded-full bg-gradient-to-b ${glowColors[type]} to-transparent`} />
      <div className="flex-1">
        <h4 className="font-semibold text-white text-sm">{title}</h4>
        {message && <p className="text-xs text-gray-400 mt-1">{message}</p>}
        {correlationId && (
          <p className="text-[10px] text-gray-500 mt-1 font-mono">
            Correlation ID: {correlationId}
          </p>
        )}
      </div>
    </motion.div>
  );
}

// Toast helper functions
export const showToast = {
  success: (title, message, correlationId = null) => 
    toast.custom(() => <PremiumToast type="success" title={title} message={message} correlationId={correlationId} />),
  error: (title, message, correlationId = null) => 
    toast.custom(() => <PremiumToast type="error" title={title} message={message} correlationId={correlationId} />),
  warning: (title, message, correlationId = null) => 
    toast.custom(() => <PremiumToast type="warning" title={title} message={message} correlationId={correlationId} />),
  info: (title, message, correlationId = null) => 
    toast.custom(() => <PremiumToast type="info" title={title} message={message} correlationId={correlationId} />),
};

// Skeleton Components
export function CardSkeleton({ className = '' }) {
  return (
    <div className={`glass-card rounded-2xl p-4 ${className}`}>
      <div className="h-4 w-24 bg-white/5 rounded shimmer mb-3" />
      <div className="h-8 w-32 bg-white/5 rounded shimmer mb-2" />
      <div className="h-3 w-20 bg-white/5 rounded shimmer" />
    </div>
  );
}

export function GameCardSkeleton() {
  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      <div className="aspect-video bg-white/5 shimmer" />
      <div className="p-4">
        <div className="h-5 w-28 bg-white/5 rounded shimmer mb-2" />
        <div className="h-4 w-20 bg-white/5 rounded shimmer" />
      </div>
    </div>
  );
}

export function ListSkeleton({ rows = 3 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="glass-card rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/5 shimmer" />
          <div className="flex-1">
            <div className="h-4 w-24 bg-white/5 rounded shimmer mb-2" />
            <div className="h-3 w-32 bg-white/5 rounded shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Empty State Component
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-12 px-4 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-violet-400" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-400 mb-4 max-w-[250px]">{description}</p>
      {action}
    </motion.div>
  );
}

// Animated Counter for balances
export function AnimatedNumber({ value, prefix = '', suffix = '', decimals = 2 }) {
  const [displayValue, setDisplayValue] = useState(0);

  useState(() => {
    const duration = 1000;
    const steps = 30;
    const stepValue = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += stepValue;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <span>
      {prefix}
      {displayValue.toLocaleString(undefined, { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
      })}
      {suffix}
    </span>
  );
}

// Confirm Dialog - Mobile-safe version
export function ConfirmDialog({ open, onClose, onConfirm, title, description }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          {/* Modal Container - Centers with mobile viewport safety */}
          <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4 z-50 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-sm pointer-events-auto max-h-[calc(100dvh-1.5rem)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2">{title}</h3>
                <p className="text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6">{description}</p>
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={onConfirm}
                    className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
